import { type ReactElement, useMemo, useState } from "react";
import { readConfig } from "../config";
import { ConfigurationRequired } from "../shell/ConfigurationRequired";
import { PageHeader } from "../shell/PageHeader";
import { Stack, Surface } from "../ui-kit";
import { AnalyticsDashboardContent } from "./AnalyticsDashboardContent";
import type { MetricGranularity } from "./fixtures";
import {
  createNoLiveAnalyticsDataSource,
  type AnalyticsDataSource,
} from "./analyticsDataSource";

/**
 * The admin portal's overview screen (ISSUE-352) — assembles the journey
 * funnel, the three product-metric widgets, and the two fixture-backed T3
 * growth charts behind one shared `PeriodSelector`. Every child component was
 * built standalone on this branch; this is the only place they are wired
 * together onto a route.
 *
 * One `granularity` value drives both growth charts (Implementation Plan
 * step 3) — the selector itself owns no chart-rendering logic, only the
 * shared value.
 */
export interface DashboardViewProps {
  /** Test-only injection point for boundary and visual-state verification. */
  readonly analyticsSource?: AnalyticsDataSource;
}

export function DashboardView({ analyticsSource }: DashboardViewProps): ReactElement {
  const config = readConfig();
  const [granularity, setGranularity] = useState<MetricGranularity>("day");
  const noLiveSource = useMemo(() => createNoLiveAnalyticsDataSource(() => undefined), []);
  const source = analyticsSource ?? noLiveSource;
  const dashboardSource: AnalyticsDataSource = {
    ...source,
    selectPeriod: (period) => {
      setGranularity(period);
      source.selectPeriod(period);
    },
  };

  return (
    <Stack gap={6}>
      <PageHeader
        title="Dashboard"
        description="The core user journey across both channels, the product's headline activation,
          retention, and engagement metrics, and growth trends over time."
      />
      {config.ok ? (
        <Stack gap={8}>
          <AnalyticsDashboardContent source={dashboardSource} granularity={granularity} />
        </Stack>
      ) : (
        <Surface as="section" padding="default">
          <ConfigurationRequired missing={config.missing} />
        </Surface>
      )}
    </Stack>
  );
}
